import { call, put, takeEvery, all } from 'redux-saga/effects';
import { purchaseOrderApi, PurchaseOrder } from '../../services/purchaseOrderApi';
import {
  fetchPurchaseOrdersStart,
  fetchPurchaseOrdersSuccess,
  fetchPurchaseOrdersFailure,
  addPurchaseOrder,
  updatePurchaseOrder,
  deletePurchaseOrder,
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
} from '../reducers/purchaseOrderSlice';
import { PayloadAction } from '@reduxjs/toolkit';

// Fetch purchase orders saga
function* fetchPurchaseOrdersApi(action: PayloadAction<any>): Generator<any, void, any> {
  try {
    const response = yield call(purchaseOrderApi.getAll, action.payload);
    yield put(fetchPurchaseOrdersSuccess(response));
  } catch (error: any) {
    yield put(fetchPurchaseOrdersFailure(error.message || 'Failed to fetch purchase orders'));
  }
}

// Create purchase order saga
function* createPurchaseOrderApi(action: PayloadAction<Omit<PurchaseOrder, 'id'>>): Generator<any, void, any> {
  try {
    const response = yield call(purchaseOrderApi.create, action.payload);
    // Refresh the purchase orders list after creating
    yield put(fetchPurchaseOrdersStart({}));
    return response;
  } catch (error: any) {
    yield put(fetchPurchaseOrdersFailure(error.message || 'Failed to create purchase order'));
  }
}

// Update purchase order saga
function* updatePurchaseOrderApi(action: PayloadAction<PurchaseOrder>): Generator<any, void, any> {
  try {
    const { id, ...purchaseOrderData } = action.payload;
    const response = yield call(purchaseOrderApi.update, id!, purchaseOrderData);
    // Refresh the purchase orders list after updating
    yield put(fetchPurchaseOrdersStart({}));
    return response;
  } catch (error: any) {
    yield put(fetchPurchaseOrdersFailure(error.message || 'Failed to update purchase order'));
  }
}

// Delete purchase order saga
function* deletePurchaseOrderApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(purchaseOrderApi.delete, action.payload);
    // Refresh the purchase orders list after deleting
    yield put(fetchPurchaseOrdersStart({}));
  } catch (error: any) {
    yield put(fetchPurchaseOrdersFailure(error.message || 'Failed to delete purchase order'));
  }
}

// Fetch dropdowns (clients, uoms, po statuses)
function* fetchDropdownsApi(): Generator<any, void, any> {
  try {
    const [clients, uoms, poStatuses] = yield all([
      call(purchaseOrderApi.getClients),
      call(purchaseOrderApi.getUoMs),
      call(purchaseOrderApi.getPOStatuses)
    ]);

    yield put(fetchDropdownsSuccess({
      clients,
      uoms,
      poStatuses
    }));
  } catch (error: any) {
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

// Root purchase order saga
export default function* purchaseOrderSaga(): Generator<any, void, any> {
  yield takeEvery(fetchPurchaseOrdersStart.type, fetchPurchaseOrdersApi);
  yield takeEvery(addPurchaseOrder.type, createPurchaseOrderApi);
  yield takeEvery(updatePurchaseOrder.type, updatePurchaseOrderApi);
  yield takeEvery(deletePurchaseOrder.type, deletePurchaseOrderApi);
  yield takeEvery(fetchDropdownsStart.type, fetchDropdownsApi);
} 